import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import Icon from "@mdi/react";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import CircularLoader from "../CircularLoader";

export interface ModalType {
  body?: string;
  title: string;
}

export interface InputType {
  icon: string;
  label: string;
  validationSchema?: yup.ObjectSchema<any>;
  initialValue?: string;
}

export interface ModalInputProps {
  cancelLabel?: string;
  input: InputType;
  modal: ModalType;
  open: boolean;
  submitLabel?: string;
  onCancel: () => void;
  onSubmit: (value: string) => Promise<void>;
}

interface FormData {
  inputValue: string;
}

function ModalInput(props: ModalInputProps) {
  const { input, modal, cancelLabel = "Cancel", submitLabel = "Save", open, onCancel, onSubmit: onSubmitProp } = props;
  const [loading, setLoading] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: { inputValue: input.initialValue ?? "" },
    resolver: input.validationSchema ? yupResolver(input.validationSchema) : undefined,
  });

  const handleDialogClose = () => {
    onCancel();
  };

  const onSubmit = async (values: FormData) => {
    setLoading(true);

    await onSubmitProp(values.inputValue);

    setLoading(false);
    handleDialogClose();
  };

  useEffect(() => {
    if (open) {
      reset({ inputValue: input.initialValue ?? "" });
    }
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={handleDialogClose} aria-labelledby='edit-user-data-dialog' maxWidth='xs'>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
        <DialogTitle id='edit-user-data-dialog'>{modal?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>{modal?.body}</DialogContentText>
          <Controller
            control={control}
            name='inputValue'
            render={({ field }) => (
              <TextField
                {...field}
                autoFocus
                error={Boolean(errors.inputValue)}
                fullWidth
                helperText={errors.inputValue?.message}
                label={input?.label || ""}
                margin='dense'
                size='small'
                InputProps={{
                  startAdornment: input?.icon && (
                    <InputAdornment position='start'>
                      <Icon path={input.icon} size={1} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <CircularLoader loading={loading}>
            <Button color='primary' disabled={loading} type='submit'>
              {submitLabel}
            </Button>
          </CircularLoader>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default ModalInput;
